import { useState } from 'react';
import {
  useTranslate,
  useNotification,
  useInvalidate,
  useCustomMutation,
} from '@refinedev/core';
import type { CrudFilter } from '@refinedev/core';
import { List } from '@refinedev/antd';
import { Table, Input, Form, Row, Col, Button } from 'antd';
import { SearchOutlined, SyncOutlined } from '@ant-design/icons';
import { extractBackendMessage } from '@/utils/apiError';
import type { MonetizationCustomerOut } from '@/api/generated/schemas';
import { useTableSearch } from '@/hooks/useTableSearch';
import { useAdminTable } from '@/hooks/useAdminTable';
import { TextFilter } from '@/components/common/TextFilter';
import { DateRangeFilter } from '@/components/common/DateRangeFilter';
import { ShowLink } from '@/components/common/ShowLink';
import { RevenueCatCustomerLink } from '@/components/monetization/RevenueCatLink';
import { isFieldFiltered } from '@/utils/filters';
import { getSortOrder } from '@/utils/sorters';

export const CustomerList = () => {
  const t = useTranslate();
  const { onSearch, createDebouncedSubmit } = useTableSearch();
  const { open: notify } = useNotification();
  const invalidate = useInvalidate();
  const { mutateAsync } = useCustomMutation();
  const [syncingId, setSyncingId] = useState<number | null>(null);

  const { tableProps, searchFormProps, sorters, setFilters, filters } =
    useAdminTable<MonetizationCustomerOut>({
      pagination: { pageSize: 20 },
      resource: 'monetization.customers',
      onSearch,
      sorters: {
        initial: [{ field: 'id', order: 'desc' }],
      },
    });

  const debouncedSubmit = createDebouncedSubmit(searchFormProps.form);

  const handleSync = async (customerId: number) => {
    setSyncingId(customerId);
    try {
      await mutateAsync({
        url: `monetization/customers/${customerId}/sync`,
        method: 'post',
        values: {},
        successNotification: false,
        errorNotification: false,
      });

      notify?.({
        type: 'success',
        message: t('notifications.success'),
        description: t('monetization.customers.sync.success'),
      });

      invalidate({
        resource: 'monetization.customers',
        invalidates: ['list', 'detail'],
      });
    } catch (error) {
      const description =
        extractBackendMessage(error) ??
        t('monetization.customers.sync.error_fallback');
      notify?.({
        type: 'error',
        message: t('notifications.error'),
        description,
      });
    } finally {
      setSyncingId(null);
    }
  };

  return (
    <List>
      <Form {...searchFormProps} layout="vertical">
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={16} md={12} lg={8}>
            <Form.Item name="q" style={{ marginBottom: 0 }}>
              <Input
                placeholder={t('monetization.customers.search.placeholder')}
                prefix={<SearchOutlined />}
                allowClear
                onChange={(e) => debouncedSubmit(e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Table
        {...tableProps}
        pagination={{ ...tableProps.pagination, simple: true }}
        rowKey="id"
      >
        <Table.Column
          dataIndex="id"
          title={t('base.fields.id')}
          sorter
          sortOrder={getSortOrder(sorters, 'id')}
          render={(value: number) => (
            <ShowLink resource="monetization.customers" id={value} />
          )}
        />

        <Table.Column
          dataIndex="account_id"
          title={t('monetization.customers.fields.account_id')}
          sorter
          sortOrder={getSortOrder(sorters, 'account_id')}
          render={(value: number) => (
            <RevenueCatCustomerLink
              id={value}
              title={t('monetization.customers.actions.open_in_revenuecat')}
              stopRowClick
            />
          )}
          filtered={isFieldFiltered(filters as CrudFilter[], 'account_id')}
          filterDropdown={({ confirm, clearFilters }) => (
            <TextFilter
              field="account_id"
              width={160}
              placeholder={t('monetization.customers.filter.account_id')}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="user_id"
          title={t('monetization.customers.fields.user_id')}
          sorter
          sortOrder={getSortOrder(sorters, 'user_id')}
          render={(value: number | null | undefined) =>
            value != null ? <ShowLink resource="iam.users" id={value} /> : '-'
          }
          filtered={isFieldFiltered(filters as CrudFilter[], 'user_id')}
          filterDropdown={({ confirm, clearFilters }) => (
            <TextFilter
              field="user_id"
              width={160}
              placeholder={t('monetization.customers.filter.user_id')}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="owner_email"
          title={t('monetization.customers.fields.owner_email')}
          render={(value: string | null | undefined) => value ?? '-'}
        />

        <Table.Column
          dataIndex="owner_username"
          title={t('monetization.customers.fields.owner_username')}
          render={(value: string | null | undefined) => value ?? '-'}
        />

        <Table.Column
          dataIndex="revenuecat_customer_id"
          title={t('monetization.customers.fields.revenuecat_customer_id')}
          filtered={isFieldFiltered(
            filters as CrudFilter[],
            'revenuecat_customer_id'
          )}
          filterDropdown={({ confirm, clearFilters }) => (
            <TextFilter
              field="revenuecat_customer_id"
              width={160}
              placeholder={t(
                'monetization.customers.filter.revenuecat_customer_id'
              )}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="last_synced_at"
          title={t('monetization.customers.fields.last_synced_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'last_synced_at')}
          filtered={isFieldFiltered(filters as CrudFilter[], 'last_synced_at')}
          filterDropdown={({ confirm, clearFilters }) => (
            <DateRangeFilter
              field="last_synced_at"
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(value: string) =>
            value ? new Date(value).toLocaleString() : '-'
          }
        />

        <Table.Column
          dataIndex="created_at"
          title={t('monetization.customers.fields.created_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'created_at')}
          render={(value: string) =>
            value ? new Date(value).toLocaleString() : '-'
          }
        />

        <Table.Column
          title={t('actions.column')}
          key="actions"
          render={(_, record: MonetizationCustomerOut) => (
            <Button
              type="text"
              size="small"
              icon={<SyncOutlined spin={syncingId === record.id} />}
              loading={syncingId === record.id}
              onClick={(e) => {
                e.stopPropagation();
                handleSync(record.id);
              }}
            >
              {t('monetization.customers.actions.sync')}
            </Button>
          )}
        />
      </Table>
    </List>
  );
};
